/**
 * Supabase persistence for the service-map store.
 *
 * Thirteen entity types. Rather than thirteen hand-written mapper pairs, the
 * shared shape (id / translations / timestamps) is handled generically and
 * each type declares only the columns that differ. Thirteen near-identical
 * mappers is exactly where a typo silently drops one field on one entity.
 *
 * Column naming is mechanical: camelCase -> snake_case. The exceptions are
 * declared per-entity in `rename`.
 */

import { cmsDb } from "./client"
import type { ServiceStore } from "../../service-map/types"
import { emptyServiceStore } from "../../service-map/types"
import type { StoreAdapter } from "./types"
import { diffRows, isEmptyDiff, getSnapshot, putSnapshot } from "./diff"

const DB_TABLE = "scms_svc_db"

/** Store bucket -> table, in dependency order (parents before children). */
interface EntitySpec {
  /** Key of the bucket on ServiceStore. */
  bucket: keyof ServiceStore
  table: string
  /** Fields that are NOT plain camelCase->snake_case, as client -> column. */
  rename?: Record<string, string>
  /** Client fields that have no column and must not be sent. */
  omit?: string[]
  /**
   * Client field standing in for the row id, when the entity has no `id`.
   *
   * Language is keyed by `code` and carries no id of its own -- the store
   * indexes it by code too. Every other entity has an ULID `id`.
   */
  idField?: string
  /**
   * False when the table has no created_at_ms / updated_at_ms columns.
   * Sending them would fail on an unknown column; omitting them where they
   * exist would fail the NOT NULL.
   */
  timestamps?: boolean
  /**
   * Columns this table actually has, beyond id/db_id/team_id/timestamps.
   *
   * REQUIRED, and the reason this exists: the agent tool surface offers ONE
   * shared field set for every entity kind, so `svc_update` can legitimately
   * put `source` or `headerImage` on a country. Those land in the store, and
   * forwarding them verbatim makes PostgREST reject the whole row --
   * "Could not find the 'source' column of 'scms_svc_country'".
   *
   * Restricting the write to real columns keeps the store permissive (which
   * the UI and tools rely on) while sending the database only what it can
   * accept. Extra client-side fields are simply not persisted.
   */
  columns: string[]
}

/*
 * ORDER MATTERS on write: a region references a country, a service references
 * a provider, and so on. Written in this order and deleted in reverse, so a
 * row never references something that does not exist yet, and nothing is
 * removed while still referenced.
 */
const ENTITIES: EntitySpec[] = [
  {
    bucket: "languages", table: "scms_svc_language",
    // Keyed by `code`, with no id and no timestamp columns.
    idField: "code", timestamps: false,
    columns: ["code", "name", "direction", "transifex", "sort"],
  },
  {
    bucket: "instances", table: "scms_svc_instance",
    columns: ["name", "status", "oldid"],
  },
  {
    bucket: "countries", table: "scms_svc_country",
    columns: [
      "name", "instanceId", "defaultLanguage", "languageCodes", "site",
      "zendeskUrl", "zendeskCategory", "oldid", "translations",
    ],
  },
  {
    bucket: "regions", table: "scms_svc_region",
    columns: ["name", "countryId", "oldid", "translations"],
  },
  {
    bucket: "cities", table: "scms_svc_city",
    columns: ["name", "countryId", "regionId", "oldid", "translations"],
  },
  {
    bucket: "providerCategories", table: "scms_svc_provider_category",
    columns: ["name", "description", "countryId", "oldid", "translations"],
  },
  {
    bucket: "serviceCategories", table: "scms_svc_service_category",
    columns: ["name", "description", "color", "icon", "status", "sort", "translations"],
  },
  {
    bucket: "tags", table: "scms_svc_tag",
    columns: ["name", "description", "translations"],
  },
  {
    bucket: "populations", table: "scms_svc_population",
    columns: ["name", "definition", "visible", "translations"],
  },
  {
    bucket: "accessibility", table: "scms_svc_accessibility",
    columns: ["name", "definition", "translations"],
  },
  {
    bucket: "providers", table: "scms_svc_provider",
    columns: [
      "name", "description", "address", "countryId", "categoryId",
      "status", "expiration", "oldid", "translations",
    ],
  },
  {
    bucket: "services", table: "scms_svc_service",
    columns: [
      "status", "name", "description",
      "countryId", "regionId", "cityId", "providerId",
      "categoryIds", "tagIds", "populationIds", "accessibilityIds",
      "physicalLocation", "address", "lat", "lon",
      "alwaysOpen", "hours", "contactInfo", "consentForms",
      "contactName", "contactLastName", "contactTitle", "contactEmail", "contactPhone",
      "secondaryName", "secondaryLastName", "secondaryTitle", "secondaryEmail", "secondaryPhone",
      "headerImage", "files", "source", "oldid", "transifexStatus", "translations",
    ],
  },
]

const snake = (s: string) => s.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`)

/** Client entity -> row. Timestamps and team/db scoping are added here.
 */
function toRow(entity: Record<string, any>, spec: EntitySpec, dbId: string, teamId: string) {
  const row: Record<string, any> = { db_id: dbId, team_id: teamId }
  const allowed = new Set(spec.columns)
  for (const [k, v] of Object.entries(entity)) {
    if (v === undefined) continue
    if (spec.omit?.includes(k)) continue
    // Only real columns. The store carries fields this table does not have
    // (see EntitySpec.columns); forwarding one rejects the entire row.
    if (k !== "id" && k !== "createdAt" && k !== "updatedAt" && !allowed.has(k)) continue
    if (k === "createdAt" || k === "updatedAt") {
      // Dropped entirely for tables without the columns; PostgREST rejects
      // an unknown column rather than ignoring it.
      if (spec.timestamps === false) continue
      row[k === "createdAt" ? "created_at_ms" : "updated_at_ms"] = v
      continue
    }
    row[spec.rename?.[k] ?? snake(k)] = v
  }
  // Entities without their own `id` still need one: the column is the primary
  // key and NOT NULL.
  if (spec.idField && row.id == null) row.id = entity[spec.idField]
  return row
}

/** Row -> client entity. Drops the scoping columns the client never sees.
 */
function fromRow(row: Record<string, any>, spec: EntitySpec) {
  const inverse: Record<string, string> = {}
  for (const [client, column] of Object.entries(spec.rename ?? {})) inverse[column] = client

  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(row)) {
    if (k === "db_id" || k === "team_id") continue
    // The id column is synthesised from idField on write; the client type has
    // no such field, so it must not come back as one.
    if (k === "id" && spec.idField) continue
    if (k === "row_created_at" || k === "row_updated_at") continue
    if (k === "created_at_ms") { out.createdAt = Number(v); continue }
    if (k === "updated_at_ms") { out.updatedAt = Number(v); continue }
    if (v === null) continue // absent, not null, in the client model
    out[inverse[k] ?? k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] = v
  }
  return out
}

export function makeServiceMapAdapter(teamId: string): StoreAdapter<ServiceStore> {
  const snapKey = (table: string, dbId: string) => `${teamId}:${dbId}:${table}`
  // The record object each snapshot row was made from. Store updates copy only what changes,
  // so the same object means an unchanged row: no rebuilding or comparing it
  const savedFrom = new Map<string, Map<string, unknown>>()

  return {
    async load(dbId) {
      const dbRes = await cmsDb.from(DB_TABLE).select("*").eq("id", dbId)
      if (dbRes.error) throw dbRes.error
      if (!dbRes.data?.length) return null

      const store = emptyServiceStore(dbRes.data[0].default_locale ?? "en-US")

      // One request per entity type, in parallel -- they are independent reads.
      const results = await Promise.all(
        ENTITIES.map(spec => cmsDb.from(spec.table).select("*").eq("db_id", dbId)),
      )

      ENTITIES.forEach((spec, i) => {
        const res = results[i]
        if (res.error) throw res.error
        const bucket: Record<string, any> = {}
        const rows: Record<string, any> = {}
        for (const r of res.data ?? []) {
          // The store indexes languages by `code`, everything else by `id`.
          // Keying the bucket wrongly here would make every lookup miss.
          const key = spec.idField ? r[snake(spec.idField)] : r.id
          bucket[key] = fromRow(r, spec)
          rows[key] = toRow(bucket[key], spec, dbId, teamId)
        }
        ;(store as any)[spec.bucket] = bucket
        putSnapshot(snapKey(spec.table, dbId), rows)
        savedFrom.set(snapKey(spec.table, dbId), new Map(Object.entries(bucket)))
      })

      return store
    },

    async save(dbId, store) {
      const { error: dbErr } = await cmsDb.from(DB_TABLE).upsert({
        id: dbId,
        team_id: teamId,
        default_locale: store.defaultLocale,
      })
      if (dbErr) throw dbErr

      const diffs = ENTITIES.map(spec => {
        const rows: Record<string, any> = {}
        const bucket = (store as any)[spec.bucket] as Record<string, any>
        const snap = getSnapshot<any>(snapKey(spec.table, dbId))
        const from = savedFrom.get(snapKey(spec.table, dbId))
        for (const e of Object.values(bucket ?? {})) {
          // Same keying rule as load(). Using `.id` for a language would key
          // every row under `undefined`, collapsing them into one.
          const key = spec.idField ? (e as any)[spec.idField] : (e as any).id
          rows[key] = snap?.[key] && from?.get(key) === e ? snap[key] : toRow(e as any, spec, dbId, teamId)
        }
        return { spec, rows, bucket, diff: diffRows(snap, rows) }
      })

      /*
       * Parents first.
       *
       * A failure on one table does NOT abandon the rest. Throwing on the
       * first error meant a single bad column took every later table with it
       * -- services, providers and geography all silently failed to save
       * because the country upsert had rejected. Each table is attempted,
       * and the first error is re-thrown at the end so the sync bridge still
       * reports the failure and retries.
       */
      let firstError: unknown = null
      const failedTables = new Set<string>()

      for (const { spec, diff } of diffs) {
        const rows = [...diff.inserted, ...diff.updated]
        if (!rows.length) continue
        const { error } = await cmsDb.from(spec.table).upsert(rows)
        if (error) {
          firstError ??= error
          failedTables.add(spec.table)
          console.error(`[smart-cms] failed to save ${spec.table}:`, error)
        }
      }

      // Children first on the way out.
      for (const { spec, diff } of [...diffs].reverse()) {
        if (!diff.deletedIds.length) continue
        const { error } = await cmsDb.from(spec.table).delete().in("id", diff.deletedIds)
        if (error) {
          firstError ??= error
          failedTables.add(spec.table)
          console.error(`[smart-cms] failed to delete from ${spec.table}:`, error)
        }
      }

      /*
       * Only tables that actually wrote get a new snapshot. Recording a
       * failed table as written would make the next diff see no change and
       * skip it forever -- the edit would be lost silently rather than
       * retried.
       */
      for (const { spec, rows, diff, bucket } of diffs) {
        if (failedTables.has(spec.table)) continue
        if (!isEmptyDiff(diff)) putSnapshot(snapKey(spec.table, dbId), rows)
        savedFrom.set(snapKey(spec.table, dbId), new Map(Object.entries(bucket ?? {})))
      }

      if (firstError) throw firstError
    },

    async clear(dbId) {
      // Deleting the db row cascades to every entity table.
      const { error } = await cmsDb.from(DB_TABLE).delete().eq("id", dbId)
      if (error) throw error
      for (const spec of ENTITIES) putSnapshot(snapKey(spec.table, dbId), {})
    },
  }
}
